import type { Tracker } from '@archipro-website/tracker';

/**
 * Where on the revamp article page a professional CTA was clicked. The pills
 * repeat down a single article, so every event carries its placement in
 * `ExtraData` — without it the series cannot tell which module converts.
 */
export type ArticleCtaPlacement =
    | 'slogan'
    /** The slogan module's bare "(Name)" attribution link, no pill pair. */
    | 'sloganAttribution'
    | 'quote'
    | 'imageParagraph'
    /** The professional's credit link in the Themes & Credits block. */
    | 'credits'
    /** The professional's logo badge in the opening row. */
    | 'badge'
    /** The professional's pill among the title's category pills. */
    | 'titlePill'
    /** The practice named in the hire cards' heading. */
    | 'hireHeading';

/**
 * Track a "View Profile" click on the revamp article page. Reuses the legacy
 * `ProductProfileButtonClick` name so the Snowflake series stays continuous
 * (legacy fired it from the article footer's CompanyProfile). Deliberately
 * not one-shot — each rendered pill pair is a distinct opportunity (BC-4876).
 */
export const trackArticleViewProfileClick = (
    tracker: Tracker,
    placement: ArticleCtaPlacement,
) => {
    tracker.log('ProductProfileButtonClick', {
        url: new URL(window.location.href),
        targetTracker: ['archiproTracker', 'ga4Tracker'],
        data: {
            ExtraData: JSON.stringify({ placement }),
        },
        ga4Data: { placement },
    });
};

/**
 * Track a "Contact" pill click on the revamp article page. New name — legacy
 * articles carried an inline enquiry form instead of linking out to the
 * professional's contact page, so this conversion path has no legacy series.
 */
export const trackArticleContactCtaClick = (
    tracker: Tracker,
    placement: ArticleCtaPlacement,
) => {
    tracker.log('ArticleContactCtaClick', {
        url: new URL(window.location.href),
        targetTracker: ['archiproTracker', 'ga4Tracker'],
        data: {
            ExtraData: JSON.stringify({ placement }),
        },
        ga4Data: { placement },
    });
};
